import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Plus, Users, BookOpen, ShoppingCart, IndianRupee, Trash2, Edit, Eye, 
  LogOut, TrendingUp, Clock, FileText, Atom, FlaskConical, Calculator, Dna,
  CheckCircle, Hash, ListChecks, GraduationCap, Target, Award, Database
} from 'lucide-react';
import QuestionManager from '../components/QuestionManager';
import ThemeToggle from '../components/ThemeToggle';

// Exam configuration with colors and icons
const EXAM_CONFIG = {
  'JEE Mains': {
    subjects: ['Physics', 'Chemistry', 'Mathematics'],
    question_types: ['MCQ', 'Numerical'],
    color: 'from-blue-500 to-cyan-500',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30',
    icon: Target
  },
  'JEE Advanced': {
    subjects: ['Physics', 'Chemistry', 'Mathematics'],
    question_types: ['MCQ', 'Numerical', 'Multiple Choice'],
    color: 'from-purple-500 to-pink-500',
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500/30',
    icon: Award
  },
  'NEET': {
    subjects: ['Physics', 'Chemistry', 'Biology'],
    question_types: ['MCQ'],
    color: 'from-green-500 to-emerald-500',
    bgColor: 'bg-green-500/10',
    borderColor: 'border-green-500/30',
    icon: GraduationCap
  }
};

const SUBJECT_ICONS = {
  'Physics': Atom,
  'Chemistry': FlaskConical,
  'Mathematics': Calculator,
  'Biology': Dna
};

const QUESTION_TYPE_ICONS = {
  'MCQ': CheckCircle,
  'Numerical': Hash,
  'Multiple Choice': ListChecks
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showQuestionsDialog, setShowQuestionsDialog] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [testSeries, setTestSeries] = useState([]);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalTestSeries: 0,
    totalPurchases: 0,
    totalRevenue: 0
  });

  const initialTestData = {
    title: '',
    description: '',
    price: '',
    questions_count: '',
    duration: '',
    difficulty: 'Medium',
    exam_type: 'JEE Mains',
    subject: 'Physics',
    question_type: 'MCQ',
    category: 'JEE Main',
    thumbnail_url: '',
    discount_percent: '0',
    offer_text: '',
    is_free: false
  };

  const [testData, setTestData] = useState(initialTestData);

  const fetchAdminData = useCallback(async () => {
    try {
      const response = await api.get('/admin/test-series-stats');
      setTestSeries(response.data);
      
      const totalPurchases = response.data.reduce((sum, test) => sum + (test.purchase_count || 0), 0);
      
      setStats({
        totalUsers: 156,
        totalTestSeries: response.data.length,
        totalPurchases: totalPurchases,
        totalRevenue: 145678
      });
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    fetchAdminData();
  }, [user, navigate, fetchAdminData]);

  const fetchQuestions = async (testId) => {
    try {
      const response = await api.get(`/admin/questions/${testId}`);
      setQuestions(response.data);
    } catch (error) {
      toast.error('Failed to load questions');
    }
  };

  const handleChange = (field, value) => {
    setTestData(prev => ({ ...prev, [field]: value }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...testData,
        price: parseFloat(testData.price),
        questions_count: parseInt(testData.questions_count),
        duration: parseInt(testData.duration),
        discount_percent: parseFloat(testData.discount_percent) || 0
      };
      await api.post('/admin/test-series', payload);
      toast.success('Test series created successfully!');
      setShowCreateForm(false);
      resetForm();
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to create test series');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...testData,
        price: parseFloat(testData.price),
        questions_count: parseInt(testData.questions_count),
        duration: parseInt(testData.duration),
        discount_percent: parseFloat(testData.discount_percent) || 0
      };
      await api.put(`/admin/test-series/${selectedTest.id}`, payload);
      toast.success('Test series updated successfully!');
      setShowEditDialog(false);
      resetForm();
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to update test series');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this test series?')) return;
    try {
      await api.delete(`/admin/test-series/${id}`);
      toast.success('Test series deleted');
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleEdit = (test) => {
    setSelectedTest(test);
    setTestData({
      title: test.title,
      description: test.description,
      price: test.price.toString(),
      questions_count: test.questions_count.toString(),
      duration: test.duration.toString(),
      difficulty: test.difficulty,
      exam_type: test.exam_type || 'JEE Mains',
      subject: test.subject || 'Physics',
      question_type: test.question_type || 'MCQ',
      category: test.category,
      thumbnail_url: test.thumbnail_url || '',
      discount_percent: (test.discount_percent || 0).toString(),
      offer_text: test.offer_text || '',
      is_free: test.is_free || false
    });
    setShowEditDialog(true);
  };

  const handleManageQuestions = async (test) => {
    setSelectedTest(test);
    await fetchQuestions(test.id);
    setShowQuestionsDialog(true);
  };

  const resetForm = () => {
    setTestData(initialTestData);
    setSelectedTest(null);
  };

  const openCreateWithCategory = (examType) => {
    const subjects = EXAM_CONFIG[examType]?.subjects || ['Physics'];
    const qTypes = EXAM_CONFIG[examType]?.question_types || ['MCQ'];
    setTestData({
      ...initialTestData,
      exam_type: examType,
      subject: subjects[0],
      question_type: qTypes[0]
    });
    setShowCreateForm(true);
  };

  const getEffectivePrice = (price, discount) => {
    return price - (price * (discount || 0) / 100);
  };

  // Helper to get exam type from test
  const getTestExamType = (test) => {
    if (test.exam_type) return test.exam_type;
    const mapping = { 'JEE Main': 'JEE Mains', 'JEE Advanced': 'JEE Advanced', 'NEET': 'NEET' };
    return mapping[test.category] || 'JEE Mains';
  };

  // Filter tests by active tab
  const filteredTests = activeTab === 'all' 
    ? testSeries 
    : testSeries.filter(t => getTestExamType(t) === activeTab);

  // Group tests by exam type for stats
  const testsByExam = {
    'JEE Mains': testSeries.filter(t => getTestExamType(t) === 'JEE Mains'),
    'JEE Advanced': testSeries.filter(t => getTestExamType(t) === 'JEE Advanced'),
    'NEET': testSeries.filter(t => getTestExamType(t) === 'NEET')
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <img 
              src="https://static.prod-images.emergentagent.com/jobs/b913f950-9aae-49dc-aba5-d378a13b23f2/images/cfb7f6d255e29e51a76f73861f035a7bbb40bada25de0eb5061520d6d7d4dab1.png" 
              alt="Pinaka Logo"
              className="h-10 w-auto cursor-pointer"
              onClick={() => navigate('/')}
            />
            <div className="h-6 w-px bg-border"></div>
            <span className="font-semibold text-primary">Admin Panel</span>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <Button variant="outline" onClick={() => navigate('/admin/question-bank')} data-testid="question-bank-btn">
              <Database className="w-4 h-4 mr-2" />
              Question Bank
            </Button>
            <Button variant="outline" onClick={() => navigate('/admin/analytics')} data-testid="analytics-btn">
              <TrendingUp className="w-4 h-4 mr-2" />
              Analytics
            </Button>
            <Button variant="outline" onClick={() => navigate('/test-series')} data-testid="browse-tests-btn">
              <Eye className="w-4 h-4 mr-2" />
              Browse Tests
            </Button>
            <Button variant="ghost" onClick={logout} data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-outfit font-bold mb-2">Welcome back, Admin!</h1>
          <p className="text-muted-foreground">Manage your test series, questions, and monitor platform activity</p>
        </motion.div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl p-5 text-white shadow-lg"
          >
            <Users className="w-8 h-8 mb-3 opacity-80" />
            <div className="text-3xl font-bold font-mono">{stats.totalUsers}</div>
            <div className="text-sm opacity-80">Total Users</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl p-5 text-white shadow-lg"
          >
            <BookOpen className="w-8 h-8 mb-3 opacity-80" />
            <div className="text-3xl font-bold font-mono">{stats.totalTestSeries}</div>
            <div className="text-sm opacity-80">Test Series</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl p-5 text-white shadow-lg"
          >
            <ShoppingCart className="w-8 h-8 mb-3 opacity-80" />
            <div className="text-3xl font-bold font-mono">{stats.totalPurchases}</div>
            <div className="text-sm opacity-80">Total Purchases</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl p-5 text-white shadow-lg"
          >
            <IndianRupee className="w-8 h-8 mb-3 opacity-80" />
            <div className="text-3xl font-bold font-mono">₹{stats.totalRevenue.toLocaleString()}</div>
            <div className="text-sm opacity-80">Total Revenue</div>
          </motion.div>
        </div>

        {/* Category Quick Stats */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          {Object.entries(EXAM_CONFIG).map(([examType, config], index) => {
            const ExamIcon = config.icon;
            const testsCount = testsByExam[examType]?.length || 0;
            const purchases = testsByExam[examType]?.reduce((sum, t) => sum + (t.purchase_count || 0), 0) || 0;
            
            return (
              <motion.div
                key={examType}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
                className={`bg-card border-2 ${config.borderColor} rounded-xl p-5 hover:shadow-lg transition-all cursor-pointer`}
                onClick={() => setActiveTab(examType)}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-xl bg-gradient-to-br ${config.color}`}>
                    <ExamIcon className="w-6 h-6 text-white" />
                  </div>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={(e) => { e.stopPropagation(); openCreateWithCategory(examType); }}
                    className="text-primary"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add
                  </Button>
                </div>
                <h3 className="font-bold text-lg mb-1">{examType}</h3>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span>{testsCount} tests</span>
                  <span>•</span>
                  <span>{purchases} purchases</span>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Test Series Management */}
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          {/* Tabs Header */}
          <div className="border-b border-border p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant={activeTab === 'all' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveTab('all')}
                data-testid="tab-all"
              >
                All Tests ({testSeries.length})
              </Button>
              {Object.entries(EXAM_CONFIG).map(([examType, config]) => (
                <Button
                  key={examType}
                  variant={activeTab === examType ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setActiveTab(examType)}
                  className={activeTab === examType ? `bg-gradient-to-r ${config.color}` : ''}
                  data-testid={`tab-${examType.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  {examType} ({testsByExam[examType]?.length || 0})
                </Button>
              ))}
            </div>
            <Button onClick={() => setShowCreateForm(true)} data-testid="create-test-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create New Test
            </Button>
          </div>

          {/* Test Cards Grid */}
          <div className="p-6">
            {filteredTests.length === 0 ? (
              <div className="text-center py-12">
                <BookOpen className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                <p className="text-muted-foreground mb-4">No tests found in this category</p>
                <Button onClick={() => openCreateWithCategory(activeTab === 'all' ? 'JEE Mains' : activeTab)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create First Test
                </Button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredTests.map((test, index) => {
                  const examType = getTestExamType(test);
                  const config = EXAM_CONFIG[examType] || EXAM_CONFIG['JEE Mains'];
                  const SubjectIcon = SUBJECT_ICONS[test.subject] || BookOpen;
                  const QTypeIcon = QUESTION_TYPE_ICONS[test.question_type] || CheckCircle;
                  
                  return (
                    <motion.div
                      key={test.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className={`bg-card border-2 ${config.borderColor} rounded-xl overflow-hidden hover:shadow-lg transition-all`}
                      data-testid={`test-card-${test.id}`}
                    >
                      {/* Card Header with gradient */}
                      <div className={`bg-gradient-to-r ${config.color} p-4 text-white`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <span className="text-xs font-semibold opacity-80 uppercase tracking-wide">{examType}</span>
                            <h3 className="font-bold text-lg line-clamp-1">{test.title}</h3>
                          </div>
                          {test.is_free && (
                            <span className="bg-white/20 backdrop-blur-sm px-2 py-1 rounded text-xs font-bold">
                              FREE
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Card Body */}
                      <div className="p-4">
                        {/* Tags */}
                        <div className="flex flex-wrap gap-2 mb-3">
                          <span className="text-xs px-2 py-1 rounded-full bg-muted flex items-center gap-1">
                            <SubjectIcon className="w-3 h-3" />
                            {test.subject || 'General'}
                          </span>
                          <span className="text-xs px-2 py-1 rounded-full bg-muted flex items-center gap-1">
                            <QTypeIcon className="w-3 h-3" />
                            {test.question_type || 'MCQ'}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            test.difficulty === 'Easy' ? 'bg-success/10 text-success' :
                            test.difficulty === 'Hard' ? 'bg-destructive/10 text-destructive' :
                            'bg-warning/10 text-warning'
                          }`}>
                            {test.difficulty}
                          </span>
                        </div>

                        {/* Stats Row */}
                        <div className="flex items-center gap-3 text-sm text-muted-foreground mb-4">
                          <div className="flex items-center gap-1">
                            <FileText className="w-4 h-4" />
                            {test.questions_count} Qs
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {test.duration}min
                          </div>
                          <div className="flex items-center gap-1">
                            <ShoppingCart className="w-4 h-4" />
                            {test.purchase_count || 0}
                          </div>
                        </div>

                        {/* Price */}
                        <div className="flex items-center justify-between mb-4">
                          <div className="font-mono font-bold text-lg">
                            {test.is_free ? (
                              <span className="text-primary">FREE</span>
                            ) : test.discount_percent > 0 ? (
                              <div className="flex items-center gap-2">
                                <span className="line-through text-muted-foreground text-sm">₹{test.price}</span>
                                <span className="text-success">₹{getEffectivePrice(test.price, test.discount_percent).toFixed(0)}</span>
                                <span className="text-xs bg-success/10 text-success px-1.5 py-0.5 rounded">
                                  {test.discount_percent}% OFF
                                </span>
                              </div>
                            ) : (
                              <span>₹{test.price}</span>
                            )}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="flex-1"
                            onClick={() => handleManageQuestions(test)}
                            data-testid={`manage-questions-${test.id}`}
                          >
                            <FileText className="w-4 h-4 mr-1" />
                            Questions
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleEdit(test)}
                            data-testid={`edit-test-${test.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => navigate(`/test-series/${test.id}`)}
                            data-testid={`preview-test-${test.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="destructive"
                            onClick={() => handleDelete(test.id)}
                            data-testid={`delete-test-${test.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Create/Edit Test Dialog */}
      <Dialog open={showCreateForm || showEditDialog} onOpenChange={(open) => {
        if (!open) {
          setShowCreateForm(false);
          setShowEditDialog(false);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-outfit">
              {showEditDialog ? 'Edit Test Series' : 'Create New Test Series'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={showEditDialog ? handleUpdate : handleCreate} className="space-y-6">
            {/* Exam Type & Title Row */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Exam Type *</Label>
                <Select 
                  value={testData.exam_type} 
                  onValueChange={(value) => {
                    handleChange('exam_type', value);
                    const subjects = EXAM_CONFIG[value]?.subjects || [];
                    if (subjects.length > 0 && !subjects.includes(testData.subject)) {
                      handleChange('subject', subjects[0]);
                    }
                    const qTypes = EXAM_CONFIG[value]?.question_types || [];
                    if (qTypes.length > 0 && !qTypes.includes(testData.question_type)) {
                      handleChange('question_type', qTypes[0]);
                    }
                  }}
                >
                  <SelectTrigger data-testid="exam-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.keys(EXAM_CONFIG).map(exam => (
                      <SelectItem key={exam} value={exam}>{exam}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Subject *</Label>
                <Select value={testData.subject} onValueChange={(value) => handleChange('subject', value)}>
                  <SelectTrigger data-testid="subject-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(EXAM_CONFIG[testData.exam_type]?.subjects || []).map(subject => (
                      <SelectItem key={subject} value={subject}>{subject}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Title */}
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={testData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="e.g., JEE Main Physics Mock Test 1"
                required
                data-testid="title-input"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea
                value={testData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Brief description of the test series..."
                required
                rows={3}
                data-testid="description-input"
              />
            </div>

            {/* Question Type, Difficulty, Questions Count, Duration */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Question Type</Label>
                <Select value={testData.question_type} onValueChange={(value) => handleChange('question_type', value)}>
                  <SelectTrigger data-testid="question-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(EXAM_CONFIG[testData.exam_type]?.question_types || ['MCQ']).map(qType => (
                      <SelectItem key={qType} value={qType}>{qType}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Difficulty</Label>
                <Select value={testData.difficulty} onValueChange={(value) => handleChange('difficulty', value)}>
                  <SelectTrigger data-testid="difficulty-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Easy">Easy</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="Hard">Hard</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Questions *</Label>
                <Input
                  type="number"
                  value={testData.questions_count}
                  onChange={(e) => handleChange('questions_count', e.target.value)}
                  placeholder="20"
                  required
                  data-testid="questions-count-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Duration (min) *</Label>
                <Input
                  type="number"
                  value={testData.duration}
                  onChange={(e) => handleChange('duration', e.target.value)}
                  placeholder="60"
                  required
                  data-testid="duration-input"
                />
              </div>
            </div>

            {/* Price & Discount */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Price (₹) *</Label>
                <Input
                  type="number"
                  value={testData.price}
                  onChange={(e) => handleChange('price', e.target.value)}
                  placeholder="499"
                  required
                  data-testid="price-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Discount %</Label>
                <Input
                  type="number"
                  value={testData.discount_percent}
                  onChange={(e) => handleChange('discount_percent', e.target.value)}
                  placeholder="0"
                  data-testid="discount-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Offer Text</Label>
                <Input
                  value={testData.offer_text}
                  onChange={(e) => handleChange('offer_text', e.target.value)}
                  placeholder="Limited time offer!"
                  data-testid="offer-text-input"
                />
              </div>
            </div>

            {/* Thumbnail URL */}
            <div className="space-y-2">
              <Label>Thumbnail URL (optional)</Label>
              <Input
                type="url"
                value={testData.thumbnail_url}
                onChange={(e) => handleChange('thumbnail_url', e.target.value)}
                placeholder="https://example.com/image.jpg"
                data-testid="thumbnail-input"
              />
            </div>

            {/* Free Test Checkbox */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="is_free"
                checked={testData.is_free}
                onChange={(e) => handleChange('is_free', e.target.checked)}
                className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                data-testid="is-free-checkbox"
              />
              <Label htmlFor="is_free" className="cursor-pointer">Mark as FREE test (for trial/demo)</Label>
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-3 pt-4">
              <Button type="submit" className="flex-1" data-testid="submit-test-btn">
                {showEditDialog ? 'Update Test Series' : 'Create Test Series'}
              </Button>
              <Button type="button" variant="outline" onClick={() => {
                setShowCreateForm(false);
                setShowEditDialog(false);
                resetForm();
              }}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Questions Manager Dialog */}
      <Dialog open={showQuestionsDialog} onOpenChange={(open) => {
        if (!open) {
          setShowQuestionsDialog(false);
          setSelectedTest(null);
          setQuestions([]);
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-outfit">
              Manage Questions - {selectedTest?.title}
            </DialogTitle>
          </DialogHeader>
          {selectedTest && (
            <QuestionManager 
              testId={selectedTest.id}
              questions={questions}
              setQuestions={setQuestions}
              questionType={selectedTest.question_type || 'MCQ'}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
